import React, { useCallback, useEffect, useMemo } from "react";

import { defineMessages, useIntl } from "react-intl";

import { Lead } from "@mapmycustomers/shared/types/entity";
import { Button } from "@mapmycustomers/ui";

import Interactive from "@app/component/map/core/controls/Interactive";
import OverlayView from "@app/component/map/core/primitives/OverlayView";
import useMap from "@app/component/map/FeaturedMap/context/useMap";
import GeoPath from "@app/types/GeoPath";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { leadIdGetter } from "@app/util/map/idGetters";

import styles from "./LassoAnnotation.module.scss";

export interface LassoAnnotationProps {
  active: boolean;
  leads: Array<Lead>;
  onHighlight: (leadIds: Set<Lead["place_id"]>) => void;
  onSelectLeads: (leadIds: Array<Lead["place_id"]>) => void;
  onUndo: () => void;
  paths: Array<GeoPath>;
}

const messages = defineMessages({
  count: {
    id: "leadFinder.lassoAnnotation.count",
    defaultMessage: "{count} {count, plural, one {lead} other {leads}} lassoed",
    description: "Number of leads lassoed",
  },
  emptySelection: {
    id: "leadFinder.lassoAnnotation.emptySelection",
    defaultMessage: "No leads lassoed",
    description: "No leads lassoed",
  },
  selectLeads: {
    id: "leadFinder.lassoAnnotation.selectLeads",
    defaultMessage: "Select {count, plural, one {lead} other {leads}}",
    description: "Select leads button title",
  },
  undo: {
    id: "leadFinder.lassoAnnotation.undo",
    defaultMessage: "Undo",
    description: "Undo button title",
  },
});

const LassoAnnotation = ({
  active,
  leads,
  onHighlight,
  onSelectLeads,
  onUndo,
  paths,
}: LassoAnnotationProps) => {
  const analytics = useExtendedAnalytics("Lasso Tool", useAnalytics());
  const intl = useIntl();
  const { map } = useMap();

  const isVisible = active && paths.length > 0 && leads.length > 0;

  const [area, bounds] = useMemo(() => {
    if (isVisible) {
      const area = new google.maps.Polygon({ paths });
      const bounds = new google.maps.LatLngBounds();
      area
        .getPath()
        .forEach((item) => bounds.extend(new google.maps.LatLng(item.lat(), item.lng())));
      return [area, bounds];
    }
    return [];
  }, [isVisible, paths]);

  const matches = useMemo(() => {
    if (isVisible && area) {
      return leads.filter(
        (lead) =>
          lead.geometry?.location &&
          google.maps.geometry.poly.containsLocation(lead.geometry.location, area)
      );
    }
  }, [area, isVisible, leads]);

  useEffect(() => {
    const highlightedItems = new Set(matches?.map((lead) => leadIdGetter(lead)));
    onHighlight(highlightedItems);
  }, [matches, onHighlight]);

  const position: google.maps.LatLngLiteral = {
    lat: bounds?.getCenter().lat() ?? 0,
    lng: bounds?.getNorthEast().lng() ?? 0,
  };

  const handleSelectClick = useCallback(() => {
    if (matches) {
      onSelectLeads(matches.map(leadIdGetter));
      analytics.completed(["Select leads"]);
    }

    onHighlight(new Set());
  }, [analytics, matches, onHighlight, onSelectLeads]);

  const handleUndoClick = useCallback(() => {
    onHighlight(new Set());
    onUndo();
    analytics.cancelled(["Undo"]);
  }, [analytics, onHighlight, onUndo]);

  const count = matches?.length ?? 0;

  return map && position && isVisible ? (
    <OverlayView map={map} position={position}>
      <div className={styles.container}>
        <div className={styles.arrow} />
        <Interactive>
          <div className={styles.header}>
            <span className={styles.count}>
              {count
                ? intl.formatMessage(messages.count, { count })
                : intl.formatMessage(messages.emptySelection)}
            </span>
            <Button className={styles.undoBtn} onClick={handleUndoClick} type="text">
              {intl.formatMessage(messages.undo)}
            </Button>
          </div>

          <div className={styles.body}>
            <Button block className={styles.btn} disabled={!count} onClick={handleSelectClick}>
              {intl.formatMessage(messages.selectLeads, { count })}
            </Button>
          </div>
        </Interactive>
      </div>
    </OverlayView>
  ) : null;
};

export default LassoAnnotation;
